<template>
  <div class="overflow-y-auto">
    <TIcon
      v-if="isLoading"
      icon="loading"
      size="lg"
      class="text-nash-purple600"
      aria-label="Loading articles"
    />
    <div v-else class="library-search-wrapper">
      <MeiliInstantSearch>
        <template #suggestion-link="{ suggestion, highlightSplitTag }">
          <div class="suggestion-wrapper">
            <div>
              <MeiliSearchHighlight
                :option="suggestion"
                :split-marker="highlightSplitTag"
                class="font-medium"
                attribute="title"
              />
              <MeiliSearchHighlight
                :option="suggestion"
                :split-marker="highlightSplitTag"
                attribute="searchableBody"
              />
            </div>
            <div class="flex gap-3 article-actions">
              <TButton
                class="view-btn"
                label="View"
                icon="outlineEye"
                @click="() => meiliSearchViewArticle(suggestion.slug)"
              />
              <TButton
                label="Copy Link"
                :icon="copiedBySlug[suggestion.slug] ? 'check' : 'square2Stack'"
                @click="() => meiliSearchCopyLink(suggestion.slug)"
              />
            </div>
          </div>
        </template>
      </MeiliInstantSearch>
      <div class="library-link">
        <TMSecondaryButton
          label="Copy library link"
          :icon="isLibraryLinkCopied ? 'check' : 'square2Stack'"
          data-testid="connect-library-link"
          @click="() => copyLibraryLink()"
        />
      </div>
    </div>
    <article class="mt-5">
      <div
        v-for="(articles, category) in articlesWithDocumentsByCategory"
        :key="category"
      >
        <p class="text-base font-medium text-nash-neutral700 pt-4 pb-5">
          {{ category }}
        </p>
        <div class="flex flex-col gap-3">
          <ArticleCard
            v-for="article in articles"
            :key="article.document?.careTeamDocumentId"
            data-testid="connect-article"
            :title="article.strapiArticleTitle"
            :connect-source-url="article.document?.sourceUrl"
            :care-team-document-id="article.document?.careTeamDocumentId"
            :external-thymebox-patient-id="externalThymeboxPatientId"
          />
        </div>
      </div>
    </article>
  </div>
</template>

<script lang="ts">
import TMSecondaryButton from '@nashville/button/TMSecondaryButton.vue'
import MeiliInstantSearch from '@thyme/libs/src/meiliSearch/MeiliInstantSearch.vue'
import MeiliSearchHighlight from '@thyme/libs/src/meiliSearch/MeiliSearchHighlight/MeiliSearchHighlight.vue'
import TButton from '@thyme/nashville/src/components/button/TButton.vue'
import TIcon from '@thyme/nashville/src/components/icon/TIcon.vue'
import { defineComponent } from 'vue'
import ArticleCard from '@/tokens/connectArticles/ArticleCard/ArticleCard.vue'
import { setup } from './controller'
import { CONNECT_ARTICLES_PROPS } from './types'

export default defineComponent({
  components: {
    TIcon,
    TButton,
    TMSecondaryButton,
    MeiliInstantSearch,
    MeiliSearchHighlight,
    ArticleCard,
  },
  props: CONNECT_ARTICLES_PROPS,
  setup,
})
</script>

<style lang="scss" scoped>
// Display the article search input and copy library link button side-by-side.
// It doesn't make sense to add the button to the MeliInstantSearch token,
// but we want the search bar and library link button while any results from
// the search still take up the full width.
.library-search-wrapper {
  @apply grid grid-cols-[1fr_auto] items-center gap-x-3;

  :deep(> .instant-search-wrapper) {
    @apply contents;
  }

  // Search results should start on the row below the search and copy library link
  // button and they should be allowed to take up the full width of the container
  :deep(> .instant-search-wrapper > .search-results-wrapper) {
    @apply col-span-full row-start-2;
  }
}

// make sure there's proper spacing between the icon on the button and button text
.library-link,
.article-actions {
  :deep(.p-button-icon) {
    @apply mr-2;
  }
}

:deep(.article-actions .view-btn) {
  @apply bg-nash-neutral000 text-nash-neutral800;
}

.suggestion-wrapper {
  @apply flex justify-between items-center;
  @apply px-6 py-4 my-4 rounded-lg;
  @apply bg-nash-neutral000 text-nash-purple600;
  @apply border border-nash-neutral400;
}
</style>
